import React, { useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, ButtonBase, Chip, Collapse, IconButton, Stack, Tooltip, Typography } from '@mui/material';

import FilterForm from './FilterForm';
import ParamsEditor from './ParamsEditor';
import { pageBlocks } from './editor';
import { layoutControls, resolvedFilterGroups } from './filterLayout';
import { useSettingsQuery } from './settingsKit';
import type { LensDocument, LensDocumentData } from './types';
import type { BoardEditor } from './useBoardEdit';

export default function FilterSettingsGroups({
  document,
  data,
  editor,
}: {
  document: LensDocument;
  data?: LensDocumentData;
  editor: BoardEditor;
}) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [configuring, setConfiguring] = useState<string>();
  const query = useSettingsQuery().trim().toLowerCase();
  const controls = new Map(layoutControls(document).map((control) => [control.id, control]));
  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden', mt: 1 }}>
      {resolvedFilterGroups(document).map((group) => {
        const all = group.controls.flatMap((id) => (controls.has(id) ? [controls.get(id)!] : []));
        const matched = all.filter(
          (control) =>
            !query || group.label.toLowerCase().includes(query) || control.label.toLowerCase().includes(query),
        );
        if (!all.length || !matched.length) return null;
        const open = Boolean(query) || Boolean(expanded[group.id]);
        return (
          <Box data-setting-row={group.label} key={group.id} sx={{ '& + &': { borderTop: 1, borderColor: 'divider' } }}>
            <ButtonBase
              aria-expanded={open}
              aria-label={`Configure ${group.label} filters`}
              onClick={() => setExpanded((current) => ({ ...current, [group.id]: !open }))}
              sx={{
                width: '100%',
                px: 1.25,
                py: 1.1,
                textAlign: 'left',
                gap: 1,
                bgcolor: open ? 'action.hover' : undefined,
              }}
            >
              <Stack sx={{ minWidth: 0, flex: 1 }} spacing={0.25}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 600, overflowWrap: 'anywhere' }}>
                  {group.label}
                </Typography>
                {!open ? (
                  <Typography color="text.secondary" noWrap sx={{ fontSize: 11 }}>
                    {all
                      .slice(0, 3)
                      .map((control) => control.label)
                      .join(', ')}
                  </Typography>
                ) : null}
              </Stack>
              <Chip label={all.length} size="small" sx={{ height: 20, fontSize: 11 }} />
              <ExpandMoreIcon
                sx={{ fontSize: 17, color: 'text.secondary', transform: open ? 'rotate(180deg)' : undefined }}
              />
            </ButtonBase>
            <Collapse in={open}>
              <Stack sx={{ px: 1, py: 0.5 }} spacing={0.25}>
                {matched.map((control) => {
                  const [kind, key] = control.id.split(':');
                  const filter = document.filters.find((entry) => entry.column === key);
                  const active = configuring === control.id;
                  return (
                    <Box data-setting-row={control.label} key={control.id}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        sx={{
                          borderRadius: 1,
                          '&:hover': { bgcolor: 'action.hover' },
                          '&:hover .filter-remove, &:focus-within .filter-remove': { opacity: 1 },
                        }}
                      >
                        <ButtonBase
                          aria-expanded={active}
                          aria-label={`Configure ${control.label} ${control.kind.toLowerCase()}`}
                          onClick={() => setConfiguring(active ? undefined : control.id)}
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            px: 0.75,
                            py: 0.9,
                            gap: 0.75,
                            justifyContent: 'flex-start',
                            textAlign: 'left',
                          }}
                        >
                          <Typography sx={{ fontSize: 12, overflowWrap: 'anywhere' }}>{control.label}</Typography>
                          {kind === 'param' ? (
                            <Typography color="text.secondary" sx={{ fontSize: 10, flexShrink: 0 }}>
                              Input
                            </Typography>
                          ) : null}
                        </ButtonBase>
                        <Tooltip title={`Remove this ${control.kind.toLowerCase()}`}>
                          <span>
                            <IconButton
                              aria-label={`Remove the ${control.label} ${control.kind.toLowerCase()}`}
                              className="filter-remove"
                              disabled={editor.pending}
                              onClick={() =>
                                editor.apply(
                                  kind === 'param'
                                    ? { op: 'param', id: key, def: null }
                                    : { op: 'filter', column: key, def: null },
                                )
                              }
                              size="small"
                              sx={{ opacity: 0, transition: 'opacity 100ms' }}
                            >
                              <CloseIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                      {active ? (
                        <Box sx={{ py: 1, px: 0.5 }}>
                          {kind === 'param' ? (
                            <ParamsEditor
                              data={data}
                              document={document}
                              editor={editor}
                              onDone={() => setConfiguring(undefined)}
                              only={key}
                            />
                          ) : (
                            <FilterForm
                              blockCount={pageBlocks(document.page).length}
                              data={data}
                              editor={editor}
                              filter={filter}
                              onDone={() => setConfiguring(undefined)}
                              taken={document.filters.map((entry) => entry.column)}
                            />
                          )}
                        </Box>
                      ) : null}
                    </Box>
                  );
                })}
              </Stack>
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
}
